/**
 * GeoInsightsDashboard — Real-time visitor analytics, light admin theme.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, AreaChart, Area, Line,
} from "recharts";
import {
  ComposableMap, Geographies, Geography, Marker, ZoomableGroup,
} from "react-simple-maps";
import {
  GlobeHemisphereWest, Desktop, Browser,
  GoogleLogo, TwitterLogo, RedditLogo, GitBranch,
  Clock, UsersThree, Eye, ArrowUp, ArrowDown, DownloadSimple,
  Lightning, Pulse, MapPin, TrendUp,
} from "@phosphor-icons/react";
import {
  adminGeoInsights, adminGeoRealtime, adminGeoDevices,
  adminGeoPages, adminGeoSources, adminGeoHourly,
  adminGeoLiveFeed, adminGeoExport, adminGeoSummary, formatApiError,
} from "@/lib/api";

// ── Design tokens (light admin system) ────────────────────────────────────

const GEO_JSON_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const ACCENT      = "#4F46E5"; // indigo-600
const ACCENT_SOFT = "#6366F1"; // indigo-500
const AXIS_TICK   = "#94a3b8"; // slate-400

const CHART_COLORS = [
  "#4F46E5", "#0EA5E9", "#10B981", "#F59E0B",
  "#EC4899", "#8B5CF6", "#14B8A6", "#F43F5E",
  "#64748B", "#6366F1",
];

const CHART_TOOLTIP = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  color: "#0f172a",
  fontSize: "11px",
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)",
};

// Country lat/lng for map markers (top ~60 countries by web traffic)
const COUNTRY_COORDS = {
  US: [-95.71, 37.09], GB: [-3.44, 55.38], DE: [10.45, 51.17], IN: [78.96, 20.59],
  BR: [-51.93, -14.24], CN: [104.2, 35.86], RU: [105.32, 61.52], AU: [133.78, -25.27],
  CA: [-106.35, 56.13], FR: [2.21, 46.23], JP: [138.25, 36.2], KR: [127.77, 35.91],
  NL: [5.29, 52.13], ES: [-3.75, 40.46], IT: [12.57, 41.87], PL: [19.14, 51.92],
  SE: [18.64, 60.13], NO: [8.47, 60.47], FI: [25.75, 61.92], DK: [10.0, 56.26],
  CH: [8.23, 46.82], AT: [14.55, 47.52], BE: [4.47, 50.5], PT: [-8.22, 39.4],
  CZ: [15.47, 49.82], HU: [19.5, 47.16], RO: [24.97, 45.94], TR: [35.24, 38.96],
  UA: [31.17, 48.38], ZA: [22.94, -30.56], NG: [8.68, 9.08], EG: [30.8, 26.82],
  MX: [-102.55, 23.63], AR: [-63.62, -38.42], CL: [-71.54, -35.68], CO: [-74.3, 4.57],
  VN: [108.28, 14.06], TH: [100.99, 15.87], ID: [113.92, -0.79], MY: [109.7, 4.21],
  PH: [121.77, 12.88], SG: [103.82, 1.35], PK: [69.35, 30.38], BD: [90.36, 23.68],
  IR: [53.69, 32.43], SA: [45.08, 23.89], AE: [53.85, 23.42], IL: [34.85, 31.05],
  NZ: [172.83, -40.9], GR: [21.82, 39.07], HK: [114.11, 22.4], TW: [120.96, 23.7],
};

// ── Utility hooks ──────────────────────────────────────────────────────────

function useInterval(fn, delay) {
  const saved = useRef(fn);
  useEffect(() => { saved.current = fn; }, [fn]);
  useEffect(() => {
    if (!delay) return;
    const id = setInterval(() => saved.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function useAnimatedCounter(target, duration = 800, skip = false) {
  const [val, setVal] = useState(0);
  const frameRef = useRef(null);
  const prevRef = useRef(0);
  useEffect(() => {
    if (skip) { setVal(target); prevRef.current = target; return; }
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    const from = prevRef.current;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const next = Math.round(from + (target - from) * ease);
      setVal(next);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        prevRef.current = target;
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [target, duration, skip]);
  return val;
}

// ── Data helpers ───────────────────────────────────────────────────────────

function withRollingAvg(data, window = 7) {
  return data.map((d, i) => {
    const slice = data.slice(Math.max(0, i - window + 1), i + 1);
    const avg = slice.reduce((sum, x) => sum + (x.count || 0), 0) / slice.length;
    return { ...d, avg: Math.round(avg) };
  });
}

function computeDeltas(daily) {
  if (!daily || daily.length < 2) return { total: null, today: null };
  const last = daily[daily.length - 1]?.count || 0;
  const prev = daily[daily.length - 2]?.count || 0;
  const today = prev > 0 ? ((last - prev) / prev) * 100 : null;
  const mid = Math.floor(daily.length / 2);
  const first = daily.slice(0, mid).reduce((s, d) => s + (d.count || 0), 0);
  const second = daily.slice(mid).reduce((s, d) => s + (d.count || 0), 0);
  const total = first > 0 ? ((second - first) / first) * 100 : null;
  return { total, today };
}

// ── Skeleton loader ────────────────────────────────────────────────────────

function Skeleton({ className = "" }) {
  return <div className={`animate-pulse motion-reduce:animate-none bg-slate-100 rounded-lg ${className}`} />;
}

// ── Delta chip ─────────────────────────────────────────────────────────────

function DeltaChip({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
      up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
    }`}>
      {up
        ? <ArrowUp size={9} weight="bold" aria-hidden />
        : <ArrowDown size={9} weight="bold" aria-hidden />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

// ── Date range selector ────────────────────────────────────────────────────

function RangeSelector({ value, onChange, options }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`geo-range-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider border rounded-md transition-colors whitespace-nowrap ${
            value === o.value
              ? "bg-indigo-600 border-indigo-600 text-white"
              : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Card shell ─────────────────────────────────────────────────────────────

function Card({ children, className = "", testId }) {
  return (
    <div data-testid={testId} className={`bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

function CardHeader({ label, icon: Icon, children }) {
  return (
    <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-100 bg-slate-50/60">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={13} className="text-slate-400" aria-hidden />}
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      {children}
    </div>
  );
}

// ── Animated stat card ─────────────────────────────────────────────────────

function MetricCard({ label, value, sub, icon: Icon, accent = false, loading = false, delta, dataTestId }) {
  const reduced = usePrefersReducedMotion();
  const animated = useAnimatedCounter(typeof value === "number" ? value : 0, 800, reduced);
  return (
    <div
      data-testid={dataTestId}
      className={`p-5 bg-white border rounded-xl shadow-sm flex flex-col gap-3 hover:border-indigo-200 transition-colors group ${
        accent ? "border-l-4 border-l-indigo-500 border-slate-200" : "border-slate-200"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && <Icon size={14} className="text-slate-300 group-hover:text-indigo-400 transition-colors" aria-hidden />}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        <div className="flex items-end gap-2">
          <div className={`font-bold text-3xl tracking-tight ${accent ? "text-indigo-600" : "text-slate-900"}`}>
            {typeof value === "number" ? animated.toLocaleString() : value}
          </div>
          {delta != null && <DeltaChip pct={delta} />}
        </div>
      )}
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}

// ── Realtime bar ───────────────────────────────────────────────────────────

function RealtimeBar({ data, loading }) {
  const count = data?.active_visitors || 0;
  return (
    <div
      aria-live="polite"
      className="flex items-center gap-4 px-5 py-3 bg-white border border-slate-200 rounded-xl shadow-sm flex-wrap"
    >
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Live</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-bold text-xl text-indigo-600">
          {loading ? "—" : count.toLocaleString()}
        </span>
        <span className="text-[10px] text-slate-400">active now</span>
      </div>
      {data?.active_pages?.length > 0 && (
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">Hot pages:</span>
          {data.active_pages.slice(0, 3).map((p) => (
            <span key={p.page} className="text-[10px] text-slate-600 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded">
              {p.page} <span className="text-indigo-500 font-semibold">·{p.count}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── World map ──────────────────────────────────────────────────────────────

function WorldMap({ topCountries, loading }) {
  const [tooltip, setTooltip] = useState(null);
  const [mapError, setMapError] = useState(false);
  const maxCount = Math.max(1, ...topCountries.map((c) => c.count));

  if (loading) return <Skeleton className="w-full h-64" />;

  const markers = topCountries
    .map((c) => ({ ...c, coords: COUNTRY_COORDS[c.country_code] }))
    .filter((c) => c.coords);

  if (mapError) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-2">
        <GlobeHemisphereWest size={28} className="text-slate-300" aria-hidden />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Map unavailable offline</p>
        <p className="text-[9px] text-slate-300">Showing country list instead</p>
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={`World map showing visitor locations across ${topCountries.length} countries`}
      data-testid="geo-world-map"
      className="relative w-full overflow-hidden bg-slate-50"
    >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 120, center: [0, 20] }}
        style={{ width: "100%", height: "320px" }}
      >
        <ZoomableGroup>
          <Geographies geography={GEO_JSON_URL} onError={() => setMapError(true)}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#e2e8f0"
                  stroke="#ffffff"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#cbd5e1", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
          {markers.map((c) => {
            const radius = 4 + Math.round((c.count / maxCount) * 14);
            return (
              <Marker
                key={c.country_code}
                coordinates={c.coords}
                onMouseEnter={() => setTooltip(c)}
                onMouseLeave={() => setTooltip(null)}
              >
                <circle r={radius} fill={ACCENT} fillOpacity={0.2} stroke={ACCENT} strokeWidth={1.5} strokeOpacity={0.7} style={{ cursor: "pointer" }} />
                <circle r={2.5} fill={ACCENT} />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>
      {tooltip && (
        <div className="absolute top-4 left-4 bg-white border border-slate-200 rounded-lg px-3 py-2 pointer-events-none z-10 shadow-md">
          <div className="flex items-center gap-2 mb-1">
            <img src={`https://flagcdn.com/w20/${tooltip.country_code.toLowerCase()}.png`} alt="" className="w-5 object-cover" />
            <span className="font-semibold text-sm text-slate-800">{tooltip.country}</span>
          </div>
          <div className="text-[10px] text-indigo-600 font-semibold">{tooltip.count.toLocaleString()} visitors</div>
        </div>
      )}
    </div>
  );
}

// ── Country flag helper ────────────────────────────────────────────────────

function Flag({ code, size = 20 }) {
  const [errored, setErrored] = useState(false);
  if (!code || code === "XX" || code === "LC") return null;
  if (errored) return (
    <span
      className="inline-flex items-center justify-center text-[8px] font-bold text-slate-400 uppercase bg-slate-100 rounded shrink-0"
      style={{ width: size, height: Math.round(size * 0.75), fontSize: Math.max(7, size * 0.4) }}
    >
      {code}
    </span>
  );
  return (
    <img
      src={`https://flagcdn.com/w${size}/${code.toLowerCase()}.png`}
      alt=""
      className="inline-block object-cover shrink-0"
      style={{ width: size, height: "auto" }}
      loading="lazy"
      onError={() => setErrored(true)}
    />
  );
}

// ── Donut chart for device/browser/os ─────────────────────────────────────

function DonutChart({ data, loading, title, icon: Icon }) {
  if (loading) return <Skeleton className="w-full h-40" />;
  if (!data?.length) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={12} className="text-slate-400" aria-hidden />}
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{title}</span>
      </div>
      <div className="flex items-center gap-4">
        <ResponsiveContainer width={100} height={100}>
          <PieChart role="img" aria-label={`${title} distribution`}>
            <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={28} outerRadius={46} strokeWidth={0}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-col gap-1.5 min-w-0">
          {data.slice(0, 5).map((d, i) => (
            <div key={d.name} className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="text-[10px] text-slate-600 truncate">{d.name}</span>
              <span className="text-[10px] text-slate-400 ml-auto shrink-0">{d.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Traffic source row ─────────────────────────────────────────────────────

const SOURCE_META = {
  google:     { label: "Google",      color: "#4285F4", icon: GoogleLogo },
  twitter:    { label: "Twitter/X",   color: "#1DA1F2", icon: TwitterLogo },
  facebook:   { label: "Facebook",    color: "#1877F2", icon: null },
  reddit:     { label: "Reddit",      color: "#FF4500", icon: RedditLogo },
  linkedin:   { label: "LinkedIn",    color: "#0A66C2", icon: null },
  direct:     { label: "Direct",      color: "#4F46E5", icon: null },
  referral:   { label: "Referral",    color: "#0EA5E9", icon: null },
  github:     { label: "GitHub",      color: "#1f2937", icon: GitBranch },
  instagram:  { label: "Instagram",   color: "#E1306C", icon: null },
  youtube:    { label: "YouTube",     color: "#FF0000", icon: null },
  bing:       { label: "Bing",        color: "#00809D", icon: null },
  duckduckgo: { label: "DuckDuckGo",  color: "#DE5833", icon: null },
};

function TrafficSourceRow({ name, count, pct, max }) {
  const meta = SOURCE_META[name] || { label: name, color: "#94a3b8", icon: null };
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
      <div className="w-5 h-5 rounded flex items-center justify-center shrink-0"
           style={{ background: meta.color + "18" }}>
        {Icon
          ? <Icon size={11} weight="fill" style={{ color: meta.color }} />
          : <div className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />}
      </div>
      <span className="text-[11px] text-slate-600 flex-1 truncate">{meta.label}</span>
      <div className="flex items-center gap-3 shrink-0">
        <div className="w-24 h-1.5 bg-slate-100 rounded-full hidden sm:block">
          <div className="h-1.5 rounded-full transition-[width] duration-500" style={{ width: `${(count / max) * 100}%`, background: meta.color }} />
        </div>
        <span className="text-[11px] text-slate-500 w-8 text-right">{pct}%</span>
        <span className="text-[10px] text-slate-400 w-12 text-right">{count.toLocaleString()}</span>
      </div>
    </div>
  );
}

// ── Live activity feed ─────────────────────────────────────────────────────

const BROWSER_META = {
  Chrome:  { color: "#4285F4" },
  Firefox: { color: "#FF7139" },
  Safari:  { color: "#006CFF" },
  Edge:    { color: "#0078D4" },
  Opera:   { color: "#FF1B2D" },
  Other:   { color: "#94a3b8" },
};

const DEVICE_META = {
  mobile:  { label: "Mobile",  color: "#10B981" },
  tablet:  { label: "Tablet",  color: "#F59E0B" },
  desktop: { label: "Desktop", color: "#6366F1" },
};

function activitySentence(event) {
  const page = event.page || "/";
  const location = event.city ? `${event.city}, ${event.country}` : event.country;
  const device = event.device || "visitor";
  const source = event.source && event.source !== "direct" ? ` via ${SOURCE_META[event.source]?.label || event.source}` : "";
  if (page === "/") return `${device} from ${location} visited homepage${source}`;
  return `${device} from ${location} viewed ${page}${source}`;
}

function FeedPill({ label, color, bg }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap"
      style={{ background: bg || color + "14", color }}
    >
      {label}
    </span>
  );
}

function LiveFeedRow({ ev, isNew, reduced }) {
  const location = ev.city ? `${ev.city}, ${ev.country}` : (ev.country || "Unknown");
  const page = ev.page || "/";
  const browser = ev.browser || "Other";
  const os = ev.os || "";
  const deviceKey = (ev.device || "desktop").toLowerCase();
  const deviceMeta = DEVICE_META[deviceKey] || DEVICE_META.desktop;
  const browserMeta = BROWSER_META[browser] || BROWSER_META.Other;
  const sourceMeta = ev.source && ev.source !== "direct" ? SOURCE_META[ev.source] : null;
  const SourceIcon = sourceMeta?.icon || null;
  const time = ev.timestamp
    ? new Date(ev.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div
      className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 transition-colors"
      style={{ animation: !reduced && isNew ? "fadeSlideIn 0.4s ease" : undefined }}
      aria-label={activitySentence(ev)}
    >
      {/* Row 1: flag + location + page + timestamp */}
      <div className="flex items-center gap-2 mb-1.5">
        <Flag code={ev.country_code} size={14} />
        <span className="text-[11px] font-semibold text-slate-700 truncate flex-1">{location}</span>
        <span
          className="text-[9px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded truncate max-w-[120px] shrink-0"
          title={page}
        >
          {page === "/" ? "/ (home)" : page}
        </span>
        <span className="text-[9px] text-slate-400 shrink-0 ml-1">{time}</span>
      </div>

      {/* Row 2: signal pills */}
      <div className="flex items-center gap-1.5 flex-wrap ml-[22px]">
        {/* Auth status — most important for marketing */}
        {ev.is_logged_in ? (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
            Logged In
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
            Guest
          </span>
        )}

        {/* Device */}
        <FeedPill label={deviceMeta.label} color={deviceMeta.color} />

        {/* Browser */}
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap"
          style={{ background: browserMeta.color + "14", color: browserMeta.color }}
        >
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: browserMeta.color }} />
          {browser}
        </span>

        {/* OS */}
        {os && os !== "Other" && (
          <FeedPill label={os} color="#64748B" bg="#f1f5f9" />
        )}

        {/* Traffic source */}
        {sourceMeta && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap"
            style={{ background: sourceMeta.color + "14", color: sourceMeta.color }}
          >
            {SourceIcon
              ? <SourceIcon size={9} weight="fill" />
              : <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: sourceMeta.color }} />}
            {sourceMeta.label}
          </span>
        )}
      </div>
    </div>
  );
}

function LiveFeed({ events, wsEvents, loading }) {
  const reduced = usePrefersReducedMotion();
  const seen = new Set();
  const combined = [...(wsEvents || []), ...(events || [])].filter((ev) => {
    const key = `${ev.timestamp}|${ev.page}|${ev.country_code}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 25);

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  if (!combined.length) {
    return (
      <div className="p-8 text-center">
        <Pulse size={24} className="text-slate-300 mx-auto mb-2" aria-hidden />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">No activity yet</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto max-h-96" aria-live="polite">
      {combined.map((ev, i) => (
        <LiveFeedRow
          key={`${ev.timestamp}|${ev.country_code}|${i}`}
          ev={ev}
          isNew={i < 3 && (wsEvents?.length || 0) > 0}
          reduced={reduced}
        />
      ))}
    </div>
  );
}

// ── Summary widget ─────────────────────────────────────────────────────────

function SummaryWidget({ summary, loading }) {
  const reduced = usePrefersReducedMotion();
  const allTime = useAnimatedCounter(summary?.all_time || 0, 800, reduced);
  const thisMonth = useAnimatedCounter(summary?.this_month || 0, 800, reduced);
  const thisYear = useAnimatedCounter(summary?.this_year || 0, 800, reduced);

  return (
    <Card testId="geo-card-summary">
      <CardHeader label="All-Time Visits" icon={TrendUp}>
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Long-term counter</span>
      </CardHeader>
      <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
        <div className="pb-4 sm:pb-0 sm:pr-5">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-1">All time</p>
          {loading ? <Skeleton className="h-8 w-24" /> : (
            <p className="font-bold text-2xl text-slate-900">{allTime.toLocaleString()}</p>
          )}
        </div>
        <div className="py-4 sm:py-0 sm:px-5">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-1">This month</p>
          {loading ? <Skeleton className="h-8 w-16" /> : (
            <p className="font-bold text-2xl text-slate-900">{thisMonth.toLocaleString()}</p>
          )}
        </div>
        <div className="pt-4 sm:pt-0 sm:pl-5 flex flex-col gap-2">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-1">This year</p>
            {loading ? <Skeleton className="h-8 w-16" /> : (
              <p className="font-bold text-2xl text-slate-900">{thisYear.toLocaleString()}</p>
            )}
          </div>
          {!loading && summary?.sparkline?.length > 0 && (
            <div>
              <p className="text-[8px] text-slate-400 mb-1">7-day trend</p>
              <ResponsiveContainer width="100%" height={36}>
                <BarChart data={summary.sparkline} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Bar dataKey="count" fill={ACCENT} radius={[2, 2, 0, 0]} />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP}
                    cursor={{ fill: "rgba(79,70,229,0.05)" }}
                    formatter={(v) => [v, "Visits"]}
                    labelFormatter={(l) => l}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── Top pages list ─────────────────────────────────────────────────────────

function TopPagesList({ pages, loading, hasMore, onLoadMore, loadingMore }) {
  if (loading) return <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  if (!pages?.length) return <p className="p-4 text-[11px] text-slate-400">No page data</p>;

  const max = Math.max(1, ...pages.map((p) => p.views));
  return (
    <div>
      {pages.map((p, i) => (
        <div key={i} className="px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] text-slate-400 w-4 shrink-0">{i + 1}</span>
            <span className="text-[11px] text-slate-700 flex-1 truncate" title={p.page}>{p.page}</span>
            <div className="flex items-center gap-2 shrink-0">
              {p.traffic_share != null && (
                <span className="text-[9px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded font-semibold">
                  {p.traffic_share}%
                </span>
              )}
              <span className="font-bold text-base text-indigo-600">{p.views.toLocaleString()}</span>
            </div>
          </div>

          <div className="ml-7 h-1 bg-slate-100 rounded-full mb-2">
            <div className="h-1 bg-indigo-200 rounded-full transition-[width] duration-500" style={{ width: `${(p.views / max) * 100}%` }} />
          </div>

          <div className="ml-7 flex items-center gap-3 flex-wrap">
            {p.unique_visitors != null && (
              <span className="text-[9px] text-slate-400">{p.unique_visitors.toLocaleString()} unique</span>
            )}
            {p.top_countries?.length > 0 ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                {p.top_countries.map((c) => (
                  <div
                    key={c.code}
                    className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-50 border border-slate-200 rounded group/country"
                    title={c.name}
                  >
                    <Flag code={c.code} size={12} />
                    <span className="text-[9px] text-slate-500 group-hover/country:text-slate-700 transition-colors">
                      {c.name}
                    </span>
                  </div>
                ))}
                {p.countries > p.top_countries.length && (
                  <span className="text-[9px] text-slate-400">+{p.countries - p.top_countries.length} more</span>
                )}
              </div>
            ) : (
              <span className="text-[9px] text-slate-400">
                {p.countries} {p.countries === 1 ? "country" : "countries"}
              </span>
            )}
            <span className="text-[9px] text-slate-400 ml-auto shrink-0">
              {p.last_seen ? new Date(p.last_seen).toLocaleDateString() : "—"}
            </span>
          </div>
        </div>
      ))}
      {hasMore && (
        <div className="p-4 border-t border-slate-100 text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            data-testid="geo-load-more"
            className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-indigo-600 px-4 py-2 border border-slate-200 rounded-lg hover:border-indigo-200 transition-colors disabled:opacity-40"
          >
            {loadingMore ? "Loading…" : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Country list ───────────────────────────────────────────────────────────

function CountryList({ countries, loading }) {
  if (loading) return <div className="space-y-2 p-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  if (!countries || countries.length === 0) return (
    <div className="p-8 text-center">
      <GlobeHemisphereWest size={28} className="text-slate-300 mx-auto mb-2" aria-hidden />
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">No visitor data yet</p>
    </div>
  );
  const max = Math.max(1, ...countries.map((c) => c.count));
  return (
    <div>
      {countries.map((c, i) => (
        <div key={c.country_code} className="px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-3 mb-1.5">
            <span className="text-[10px] text-slate-400 w-4 shrink-0">{i + 1}</span>
            <Flag code={c.country_code} size={18} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-800 truncate">{c.country}</div>
              <div className="text-[9px] text-slate-400 uppercase">{c.country_code}</div>
            </div>
            <div className="font-bold text-lg text-indigo-600 shrink-0">{c.count.toLocaleString()}</div>
          </div>
          <div className="ml-7 h-1 bg-slate-100 rounded-full">
            <div className="h-1 bg-indigo-300 rounded-full transition-[width] duration-500" style={{ width: `${(c.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Hourly chart ───────────────────────────────────────────────────────────

function HourlyChart({ data, loading }) {
  if (loading) return <Skeleton className="w-full h-40" />;
  if (!data || data.length === 0) return (
    <div className="flex items-center justify-center h-40 text-[11px] text-slate-400">No hourly data yet</div>
  );
  const peakHour = data.reduce((best, d) => (d.count > best.count ? d : best), data[0]);

  return (
    <div>
      <div className="mb-3 text-[10px] text-slate-500">
        Peak hour: <span className="text-slate-700 font-semibold">{peakHour.label}</span>
        {" "}·{" "}
        <span className="text-indigo-600 font-semibold">{peakHour.count.toLocaleString()} visitors</span>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: -24 }}>
          <XAxis
            dataKey="label"
            tick={{ fill: AXIS_TICK, fontSize: 8 }}
            tickFormatter={(v, i) => (i % 4 === 0 ? v : "")}
            axisLine={false}
            tickLine={false}
          />
          <YAxis tick={{ fill: AXIS_TICK, fontSize: 8 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={CHART_TOOLTIP} cursor={{ fill: "rgba(79,70,229,0.05)" }} formatter={(v) => [v, "Visitors"]} />
          <Bar dataKey="count" radius={[2, 2, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.hour} fill={d.hour === peakHour.hour ? ACCENT : "#e2e8f0"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Top cities (live feed aggregate) ──────────────────────────────────────

function TopCitiesCard({ wsEvents, feedEvents }) {
  const cityMap = {};
  [...(wsEvents || []), ...(feedEvents || [])].forEach((ev) => {
    if (ev.city) cityMap[ev.city] = (cityMap[ev.city] || 0) + 1;
  });
  const cities = Object.entries(cityMap).sort(([, a], [, b]) => b - a).slice(0, 12);
  if (!cities.length) return null;

  return (
    <Card testId="geo-card-cities">
      <CardHeader label="Top Cities · recent activity" icon={MapPin}>
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">from live feed</span>
      </CardHeader>
      <div className="flex flex-wrap gap-2 p-4">
        {cities.map(([city, count]) => (
          <div key={city} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-50 border border-indigo-100 rounded-lg">
            <span className="text-[10px] font-semibold text-indigo-700">{city}</span>
            <span className="text-[10px] text-indigo-400 font-bold">·{count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Main dashboard ─────────────────────────────────────────────────────────

const DAY_OPTIONS = [
  { value: 1,   label: "1d"   },
  { value: 2,   label: "2d"   },
  { value: 7,   label: "7d"   },
  { value: 14,  label: "14d"  },
  { value: 30,  label: "30d"  },
  { value: 90,  label: "90d"  },
  { value: 180, label: "180d" },
  { value: 365, label: "1y"   },
];

export default function GeoInsightsDashboard() {
  const [days, setDays] = useState(30);
  const [base, setBase] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [devices, setDevices] = useState(null);
  const [pages, setPages] = useState(null);
  const [allPages, setAllPages] = useState([]);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [pagesPage, setPagesPage] = useState(1);
  const [loadingMorePages, setLoadingMorePages] = useState(false);
  const [sources, setSources] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [feed, setFeed] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [wsEvents, setWsEvents] = useState([]);
  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingSecondary, setLoadingSecondary] = useState(true);
  const [err, setErr] = useState("");
  const [exporting, setExporting] = useState(false);
  const wsRef = useRef(null);

  // ── Load primary data (changes with day range) ──────────────────────────
  const loadBase = useCallback(() => {
    setLoadingBase(true);
    Promise.all([adminGeoInsights(days, 12)])
      .then(([b]) => { setBase(b); setErr(""); })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoadingBase(false));
  }, [days]);

  // ── Load secondary data (devices, pages, sources, hourly) ──────────────
  const loadSecondary = useCallback(() => {
    setLoadingSecondary(true);
    setPagesPage(1);
    Promise.all([
      adminGeoDevices(days),
      adminGeoPages(days, 1, 10),
      adminGeoSources(days),
      adminGeoHourly(Math.min(days, 30)),
      adminGeoLiveFeed(20),
    ])
      .then(([d, p, s, h, f]) => {
        setDevices(d);
        setPages(p);
        setAllPages(p?.top_pages || []);
        setHasMorePages(p?.has_more ?? false);
        setSources(s);
        setHourly(h);
        setFeed(f);
      })
      .catch(() => {})
      .finally(() => setLoadingSecondary(false));
  }, [days]);

  // ── Load summary (once, not day-dependent) ───────────────────────────────
  const loadSummary = useCallback(() => {
    adminGeoSummary().then(setSummary).catch(() => {}).finally(() => setLoadingSummary(false));
  }, []);

  // ── Realtime polling (every 15 s) ────────────────────────────────────────
  const loadRealtime = useCallback(() => {
    adminGeoRealtime().then(setRealtime).catch(() => {});
  }, []);

  useEffect(() => { loadBase(); loadSecondary(); loadRealtime(); loadSummary(); }, [loadBase, loadSecondary, loadRealtime, loadSummary]);
  useInterval(loadRealtime, 15000);
  useInterval(loadSecondary, 60000);

  const handleLoadMorePages = useCallback(async () => {
    const nextPage = pagesPage + 1;
    setLoadingMorePages(true);
    try {
      const result = await adminGeoPages(days, nextPage, 10);
      setAllPages((prev) => [...prev, ...(result?.top_pages || [])]);
      setHasMorePages(result?.has_more ?? false);
      setPagesPage(nextPage);
    } catch {} finally {
      setLoadingMorePages(false);
    }
  }, [days, pagesPage]);

  // ── WebSocket for live feed ──────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("tm_token") || "";
    const wsUrl = (process.env.REACT_APP_BACKEND_URL || "")
      .replace(/^http/, "ws") + `/ws/geo-insights?token=${encodeURIComponent(token)}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === "new_visit") {
            setWsEvents((prev) => [data, ...prev].slice(0, 20));
            setRealtime((prev) => prev ? { ...prev, active_visitors: (prev.active_visitors || 0) + 1 } : prev);
          }
        } catch {}
      };
      const ping = setInterval(() => { if (ws.readyState === 1) ws.send("ping"); }, 30000);
      return () => { clearInterval(ping); ws.close(); };
    } catch {
      return () => {};
    }
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try { await adminGeoExport(days); } catch {} finally { setExporting(false); }
  };

  if (err) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-xl p-6 text-center">
        <p className="text-[11px] text-red-500 font-semibold">{err}</p>
      </div>
    );
  }

  const topCountries = base?.top_countries || [];
  const dailyVisitors = base?.daily_visitors || [];
  const monthlyVisitors = base?.monthly_visitors || [];
  const sourceList = sources?.sources || [];
  const maxSource = Math.max(1, ...sourceList.map((s) => s.count));
  const deltas = computeDeltas(dailyVisitors);
  const dailyWithAvg = withRollingAvg(dailyVisitors);

  return (
    <div className="space-y-6" data-testid="geo-insights-dashboard">

      {/* ── Header row: title + range + export ─────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GlobeHemisphereWest size={14} weight="fill" className="text-indigo-500" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Geo Insights
            </span>
          </div>
          <h2 className="font-bold text-2xl tracking-tight text-slate-900">Visitor Analytics</h2>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Real data only — bots, crawlers, and AI scrapers are automatically excluded. Every count here is a genuine human visit.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RangeSelector value={days} onChange={setDays} options={DAY_OPTIONS} />
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="geo-export-csv"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors text-[10px] font-semibold uppercase tracking-wider rounded-lg"
          >
            <DownloadSimple size={12} aria-hidden />
            {exporting ? "…" : "CSV"}
          </button>
        </div>
      </div>

      {/* ── Realtime bar ────────────────────────────────────────────── */}
      <RealtimeBar data={realtime} loading={!realtime} />
      <p className="text-[9px] text-slate-400 -mt-4 ml-1">
        Bots &amp; AI crawlers filtered out — only real human interactions are counted.
      </p>

      {/* ── Summary widget (long-term counter) ──────────────────────── */}
      <SummaryWidget summary={summary} loading={loadingSummary} />

      {/* ── Metric cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard label="Total visitors" value={base?.total_visitors || 0} icon={UsersThree} accent loading={loadingBase} delta={deltas.total} dataTestId="geo-metric-total" />
        <MetricCard label="Countries reached" value={base?.total_countries || 0} icon={GlobeHemisphereWest} loading={loadingBase} dataTestId="geo-metric-countries" />
        <MetricCard label="Today" value={base?.today_visitors || 0} icon={TrendUp} loading={loadingBase} delta={deltas.today} dataTestId="geo-metric-today" />
        <MetricCard
          label="Last active"
          value={base?.last_active ? new Date(base.last_active).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
          icon={Clock}
          loading={loadingBase}
          dataTestId="geo-metric-last-active"
        />
        <MetricCard label="Logged-in" value={base?.logged_in_visitors || 0} icon={UsersThree} accent loading={loadingBase} dataTestId="geo-metric-logged-in" />
        <MetricCard label="Anonymous" value={base?.anonymous_visitors || 0} icon={Eye} loading={loadingBase} dataTestId="geo-metric-anonymous" />
      </div>

      {/* ── World map + top countries ─────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <Card className="xl:col-span-2">
          <CardHeader label="Visitor World Map" icon={MapPin} />
          <WorldMap topCountries={topCountries} loading={loadingBase} />
        </Card>

        <Card>
          <CardHeader label="Top Countries" icon={GlobeHemisphereWest}>
            <span className="text-[10px] text-slate-400">{topCountries.length} countries</span>
          </CardHeader>
          <div className="max-h-72 overflow-y-auto">
            <CountryList countries={topCountries} loading={loadingBase} />
          </div>
        </Card>
      </div>

      {/* ── Daily visitors + 7-day rolling avg ──────────────────────── */}
      <Card>
        <CardHeader label={`Daily Visitors · last ${days} days`} icon={TrendUp}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-0.5 bg-indigo-600 rounded" />
              <span className="text-[9px] text-slate-500">visitors</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 border-t border-dashed border-indigo-400" />
              <span className="text-[9px] text-slate-500">7-day avg</span>
            </div>
            <RangeSelector value={days} onChange={setDays} options={DAY_OPTIONS} />
          </div>
        </CardHeader>
        <div className="p-5">
          {loadingBase ? <Skeleton className="w-full h-44" /> : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={dailyWithAvg} margin={{ top: 4, right: 0, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="visitorGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ACCENT} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: AXIS_TICK, fontSize: 9 }}
                  tickFormatter={(v) => v.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: AXIS_TICK, fontSize: 9 }}
                  axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={CHART_TOOLTIP} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }}
                  formatter={(v, n) => [v, n === "avg" ? "7-day avg" : "Visitors"]} />
                <Area dataKey="count" stroke={ACCENT} strokeWidth={2} fill="url(#visitorGrad)" dot={false}
                  activeDot={{ r: 4, fill: ACCENT, stroke: "#fff", strokeWidth: 2 }} name="visitors" />
                <Line dataKey="avg" stroke={ACCENT_SOFT} strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="avg" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* ── Monthly trend ────────────────────────────────────────────── */}
      {monthlyVisitors.length > 0 && (
        <Card>
          <CardHeader label="Monthly Visitors" icon={TrendUp} />
          <div className="p-5">
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={monthlyVisitors} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <XAxis dataKey="month" tick={{ fill: AXIS_TICK, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: AXIS_TICK, fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={CHART_TOOLTIP} cursor={{ fill: "rgba(79,70,229,0.05)" }} formatter={(v) => [v, "Visitors"]} />
                <Bar dataKey="count" fill={ACCENT} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* ── Hourly traffic + Live feed ────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <Card>
          <CardHeader label="Hourly Traffic Distribution" icon={Clock} />
          <div className="p-5">
            <HourlyChart data={hourly?.hourly || []} loading={loadingSecondary} />
          </div>
        </Card>

        <Card>
          <CardHeader label="Live Activity Feed" icon={Pulse}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
          </CardHeader>
          <LiveFeed events={feed?.events || []} wsEvents={wsEvents} loading={loadingSecondary} />
        </Card>
      </div>

      {/* ── Device analytics + Traffic sources ───────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card testId="geo-card-devices">
          <CardHeader label="Device & Browser Analytics" icon={Desktop} />
          <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-6">
            <DonutChart data={devices?.devices || []} loading={loadingSecondary} title="Devices" icon={Desktop} />
            <DonutChart data={devices?.browsers || []} loading={loadingSecondary} title="Browsers" icon={Browser} />
            <DonutChart data={devices?.os || []} loading={loadingSecondary} title="Operating Systems" icon={Desktop} />
            <DonutChart
              data={[
                { name: "Logged-in", count: base?.logged_in_visitors || 0 },
                { name: "Anonymous", count: base?.anonymous_visitors || 0 },
              ].filter((d) => d.count > 0)}
              loading={loadingBase}
              title="Auth Split"
              icon={UsersThree}
            />
          </div>
        </Card>

        <Card testId="geo-card-sources">
          <CardHeader label="Traffic Sources" icon={ArrowUp} />
          <div className="px-5 py-3">
            {loadingSecondary ? (
              <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
            ) : sourceList.length ? (
              sourceList.map((s) => <TrafficSourceRow key={s.name} {...s} max={maxSource} />)
            ) : (
              <p className="text-[11px] text-slate-400 py-4">No source data yet</p>
            )}
          </div>
        </Card>
      </div>

      {/* ── Top pages ────────────────────────────────────────────────── */}
      <Card testId="geo-card-pages">
        <CardHeader label="Top Pages" icon={Eye}>
          <span className="text-[10px] text-slate-400">{allPages.length} pages tracked</span>
        </CardHeader>
        <TopPagesList
          pages={allPages}
          loading={loadingSecondary}
          hasMore={hasMorePages}
          onLoadMore={handleLoadMorePages}
          loadingMore={loadingMorePages}
        />
      </Card>

      {/* ── Top cities (from live feed) ───────────────────────────────── */}
      <TopCitiesCard wsEvents={wsEvents} feedEvents={feed?.events || []} />

      {/* ── Active countries (realtime) ───────────────────────────────── */}
      {realtime?.active_countries?.length > 0 && (
        <Card testId="geo-card-active-countries">
          <CardHeader label="Active Right Now · by Country" icon={Lightning} />
          <div className="flex flex-wrap gap-2 p-4">
            {realtime.active_countries.map((c) => (
              <div key={c.country_code}
                className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-lg">
                <Flag code={c.country_code} size={14} />
                <span className="text-[10px] text-slate-600">{c.country}</span>
                <span className="text-[10px] font-bold text-indigo-600">{c.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

    </div>
  );
}
